import React, { useState, useRef, useCallback, useEffect } from "react";
import { Modal } from "antd";

const CROP_SIZE_DESKTOP = 320; // diameter of the circular crop area in px
const CONTAINER_SIZE_DESKTOP = 390; // outer rounded-rect container size in px
const CROP_SIZE_MOBILE = 220;
const CONTAINER_SIZE_MOBILE = 270;
const PREVIEW_SIZES_DESKTOP = [80, 60, 40, 28]; // preview circle diameters
const PREVIEW_SIZES_MOBILE = [56, 44, 32, 24];
const SQUARE_PREVIEW_SIZES_DESKTOP = [80, 60, 40, 28]; // preview square sizes
const SQUARE_PREVIEW_SIZES_MOBILE = [56, 44, 32, 24];
const MAX_SCALE = 3;
const OUTPUT_SIZE = 400; // canvas output diameter
const MOBILE_BREAKPOINT = 768;

interface ImageCropperModalProps {
  open: boolean;
  onClose: () => void;
  /** Object URL or data URL of the image to crop */
  imageSrc: string;
  /** Called with the circular-cropped File on save */
  onSave: (croppedFile: File) => void;
  /** Called when the user clicks "Delete current image" */
  onDelete?: () => void;
  /** Called when the user picks a replacement image from within the modal */
  onChangeImage?: (file: File, url: string) => void;
}

const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  open,
  onClose,
  imageSrc,
  onSave,
  onDelete,
  onChangeImage,
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const CROP_SIZE = isMobile ? CROP_SIZE_MOBILE : CROP_SIZE_DESKTOP;
  const CONTAINER_SIZE = isMobile
    ? CONTAINER_SIZE_MOBILE
    : CONTAINER_SIZE_DESKTOP;
  const PREVIEW_SIZES = isMobile ? PREVIEW_SIZES_MOBILE : PREVIEW_SIZES_DESKTOP;
  const SQUARE_PREVIEW_SIZES = isMobile
    ? SQUARE_PREVIEW_SIZES_MOBILE
    : SQUARE_PREVIEW_SIZES_DESKTOP;

  const dragStartRef = useRef<{
    x: number;
    y: number;
    posX: number;
    posY: number;
  } | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state whenever the modal opens or the image source changes
  useEffect(() => {
    if (open) {
      setPosition({ x: 0, y: 0 });
      setRotation(0);
    }
  }, [open, imageSrc]);

  // Set the initial zoom so the image fills (covers) the crop circle
  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;
    const { naturalWidth, naturalHeight } = img;
    const computed = Math.max(
      CROP_SIZE / naturalWidth,
      CROP_SIZE / naturalHeight,
    );
    setMinScale(computed);
    setScale(computed);
    setPosition({ x: 0, y: 0 });
  }, [CROP_SIZE]);

  // --- Drag (mouse) ---
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        posX: position.x,
        posY: position.y,
      };
    },
    [position],
  );

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStartRef.current) return;
    setPosition({
      x: dragStartRef.current.posX + e.clientX - dragStartRef.current.x,
      y: dragStartRef.current.posY + e.clientY - dragStartRef.current.y,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  // --- Drag (touch) ---
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      setIsDragging(true);
      dragStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        posX: position.x,
        posY: position.y,
      };
    },
    [position],
  );

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!dragStartRef.current) return;
    const touch = e.touches[0];
    setPosition({
      x: dragStartRef.current.posX + touch.clientX - dragStartRef.current.x,
      y: dragStartRef.current.posY + touch.clientY - dragStartRef.current.y,
    });
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: true });
      window.addEventListener("touchend", handleTouchEnd);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    isDragging,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ]);

  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  // --- Canvas export ---
  const handleSave = () => {
    const img = imageRef.current;
    if (!img || !img.complete || img.naturalWidth === 0) return;

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d")!;

    // Square crop — no circular clip
    const factor = OUTPUT_SIZE / CROP_SIZE;
    ctx.translate(
      OUTPUT_SIZE / 2 + position.x * factor,
      OUTPUT_SIZE / 2 + position.y * factor,
    );
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale * factor, scale * factor);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "cropped-image.png", { type: "image/png" });
      onSave(file);
      onClose();
    }, "image/png");
  };

  // --- Change image ---
  const handleChangeImageClick = () => fileInputRef.current?.click();

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    onChangeImage?.(file, url);
    e.target.value = "";
  };

  // Shared image transform style builder
  const imgStyle = (
    px: number,
    py: number,
    s: number,
    r: number,
  ): React.CSSProperties => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: `translate(calc(-50% + ${px}px), calc(-50% + ${py}px)) rotate(${r}deg) scale(${s})`,
    maxWidth: "none",
    userSelect: "none",
    pointerEvents: "none",
    transformOrigin: "center",
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={isMobile ? "94vw" : 860}
      centered
      zIndex={30000}
      styles={{
        body: { padding: isMobile ? "20px 16px 16px" : "28px 28px 24px" },
      }}
    >
      {/* ── Header ── */}
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-lg md:text-xl font-bold font-grotesk text-[#101828]">
          Edit photo
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5l10 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center md:items-start">
        {/* Left – crop area */}
        <div className="flex flex-col items-center gap-4 md:gap-5 flex-shrink-0 w-full md:w-auto">
          {/* Rounded container with blurred bg + sharp circle overlay */}
          <div
            style={{
              width: CONTAINER_SIZE,
              height: CONTAINER_SIZE,
              borderRadius: 16,
              background: "#f5f5f5",
              position: "relative",
              overflow: "hidden",
              cursor: isDragging ? "grabbing" : "grab",
              flexShrink: 0,
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {/* Blurred background (outside-circle ambience) */}
            <img
              src={imageSrc}
              alt=""
              draggable={false}
              style={{
                ...imgStyle(position.x, position.y, scale, rotation),
                filter: "blur(10px)",
                opacity: 0.55,
              }}
            />

            {/* Sharp circular crop */}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: CROP_SIZE,
                height: CROP_SIZE,
                borderRadius: "50%",
                overflow: "hidden",
                zIndex: 1,
              }}
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt="crop"
                draggable={false}
                onLoad={handleImageLoad}
                style={imgStyle(position.x, position.y, scale, rotation)}
              />
            </div>
          </div>

          {/* Zoom + Rotate controls */}
          <div
            className="flex items-center gap-3"
            style={{ width: CONTAINER_SIZE }}
          >
            {/* Zoom out */}
            <button
              onClick={() =>
                setScale((s) => Math.max(minScale, +(s - 0.1).toFixed(2)))
              }
              className="text-[#667085] hover:text-[#344054] flex-shrink-0 transition-colors"
              aria-label="Zoom out"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path
                  d="M8 11h6M20 20l-3-3"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Slider */}
            <input
              type="range"
              min={Math.round(minScale * 100)}
              max={MAX_SCALE * 100}
              value={Math.round(scale * 100)}
              onChange={(e) => setScale(Number(e.target.value) / 100)}
              className="flex-1 h-1 cursor-pointer accent-[#FF6929]"
            />

            {/* Zoom in */}
            <button
              onClick={() =>
                setScale((s) => Math.min(MAX_SCALE, +(s + 0.1).toFixed(2)))
              }
              className="text-[#667085] hover:text-[#344054] flex-shrink-0 transition-colors"
              aria-label="Zoom in"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path
                  d="M8 11h6M11 8v6M20 20l-3-3"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* Divider */}
            <div className="w-px h-5 bg-[#D0D5DD] flex-shrink-0" />

            {/* Rotate 90° */}
            <button
              onClick={handleRotate}
              className="text-[#667085] hover:text-[#344054] flex-shrink-0 transition-colors"
              aria-label="Rotate 90°"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 2v6h-6"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 12a9 9 0 0115.9-5.7L21 8"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Right – description + live previews */}
        <div className="flex flex-col flex-1 justify-between pt-0 md:pt-2 w-full">
          <div>
            <h3 className="text-[17px] font-bold font-grotesk text-[#101828] leading-snug mb-3">
              Preview
            </h3>
            <p className="text-sm text-[#667085] leading-relaxed">
              See how your new profile picture will look in different sizes
              across the platform. Make sure to save your changes!
            </p>
          </div>

          {/* Scaled live-preview circles */}
          <div className="flex items-end gap-3 md:gap-4 mt-3 md:mt-1 flex-wrap">
            {PREVIEW_SIZES.map((size) => {
              const ratio = size / CROP_SIZE;
              return (
                <div
                  key={size}
                  style={{
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    overflow: "hidden",
                    background: "#e5e7eb",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={imageSrc}
                    alt="preview"
                    draggable={false}
                    style={imgStyle(
                      position.x * ratio,
                      position.y * ratio,
                      scale * ratio,
                      rotation,
                    )}
                  />
                </div>
              );
            })}
          </div>

          {/* Scaled live-preview squares */}
          <div className="flex items-end gap-3 md:gap-4 mt-4 md:mt-5 flex-wrap">
            {SQUARE_PREVIEW_SIZES.map((size) => {
              const ratio = size / CROP_SIZE;
              return (
                <div
                  key={size}
                  style={{
                    width: size,
                    height: size,
                    borderRadius: 6,
                    overflow: "hidden",
                    background: "#e5e7eb",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={imageSrc}
                    alt="square preview"
                    draggable={false}
                    style={imgStyle(
                      position.x * ratio,
                      position.y * ratio,
                      scale * ratio,
                      rotation,
                    )}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 mt-6 md:mt-8 pt-5 border-t border-[#F2F4F7]">
        {/* Delete */}
        <button
          onClick={onDelete}
          className="hidden sm:flex items-center gap-2 text-[#D44333] hover:opacity-75 text-sm font-medium transition-opacity opacity-0"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Delete current image
        </button>

        {/* Change + Save */}
        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
          <button
            onClick={handleChangeImageClick}
            className="text-[#FF6929] font-semibold text-sm hover:opacity-75 transition-opacity"
          >
            Change image
          </button>
          <button
            onClick={handleSave}
            className="bg-[#000000] text-white font-semibold text-sm px-5 sm:px-6 py-2.5 rounded-full hover:opacity-80 transition-opacity"
          >
            Save photo
          </button>
        </div>
      </div>

      {/* Hidden file input – "Change image" */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileInputChange}
      />
    </Modal>
  );
};

export default ImageCropperModal;
